/**
 * Collection-site scoping for flat permission strings (waste outlet).
 * See frontend-auth/11-user-managment-page.md §1.8 / sites + flat permissions.
 */

export function replacePermissionSiteSegment(
  permission: string,
  orgId: string,
  siteId: string,
): string {
  const parts = permission.split(".");
  if (parts.length >= 4 && parts[0] === "recyclee" && parts[1] === orgId) {
    parts[2] = siteId;
    return parts.join(".");
  }
  return permission;
}

/** Catalog/editor site id: * when all sites or multiple sites are selected. */
export function editorSiteIdForCollectionSites(siteIds: string[] | "*"): string {
  if (siteIds === "*") return "*";
  if (siteIds.length === 1) return siteIds[0];
  return "*";
}

/**
 * Normalize permissions for API save.
 * - All sites → `recyclee.{orgId}.*.{entity}.{action}`
 * - N specific sites → duplicate each permission per site id
 */
export function expandPermissionsForCollectionSites(
  permissions: readonly string[],
  orgId: string,
  siteIds: string[] | "*",
): string[] {
  if (siteIds === "*") {
    const seen = new Set<string>();
    for (const p of permissions) {
      seen.add(replacePermissionSiteSegment(p, orgId, "*"));
    }
    return [...seen];
  }
  if (siteIds.length === 0) {
    return [...permissions];
  }
  const seen = new Set<string>();
  for (const siteId of siteIds) {
    for (const p of permissions) {
      seen.add(replacePermissionSiteSegment(p, orgId, siteId));
    }
  }
  return [...seen];
}

/** Collapse duplicated per-site permissions to a * template for the editor. */
export function normalizePermissionsForEditor(
  permissions: readonly string[],
  orgId: string,
  siteIds: string[] | "*",
): string[] {
  if (siteIds === "*") {
    return expandPermissionsForCollectionSites(permissions, orgId, "*");
  }
  if (siteIds.length <= 1) {
    return [...permissions];
  }
  const siteSet = new Set(siteIds);
  const seen = new Set<string>();
  for (const p of permissions) {
    const parts = p.split(".");
    if (
      parts.length >= 4 &&
      parts[0] === "recyclee" &&
      parts[1] === orgId &&
      siteSet.has(parts[2])
    ) {
      seen.add(replacePermissionSiteSegment(p, orgId, "*"));
    } else {
      seen.add(p);
    }
  }
  return [...seen];
}
